export type MyAdventureStatus = 'organising' | 'going' | 'interested' | 'waitlist';

type AdventureForGrouping = {
  id: string;
  user_id?: string | null;
  date_start: string;
  date_end?: string | null;
};

export type MyAdventureBuckets<T extends AdventureForGrouping> = {
  organising: Array<T & { my_adventure_status: MyAdventureStatus; is_past: boolean }>;
  upcoming: Array<T & { my_adventure_status: MyAdventureStatus; is_past: boolean }>;
  past: Array<T & { my_adventure_status: MyAdventureStatus; is_past: boolean }>;
};

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const parseAdventureTime = (value: string, endOfDay: boolean) => {
  const normalized = DATE_ONLY_PATTERN.test(value)
    ? `${value}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}`
    : value;
  const parsed = new Date(normalized).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getAdventureEndTime = (adventure: AdventureForGrouping) => {
  const rawEnd = adventure.date_end ?? adventure.date_start;
  return parseAdventureTime(rawEnd, true) || parseAdventureTime(adventure.date_start, true);
};

const getAdventureStartTime = (adventure: AdventureForGrouping) => {
  return parseAdventureTime(adventure.date_start, false);
};

export function orderAdventuresByNextOccurrence<T extends AdventureForGrouping>(
  adventures: readonly T[],
  now = new Date(),
): T[] {
  const nowMs = now.getTime();

  return [...adventures].sort((left, right) => {
    const leftStart = getAdventureStartTime(left);
    const rightStart = getAdventureStartTime(right);
    const leftEnd = getAdventureEndTime(left);
    const rightEnd = getAdventureEndTime(right);
    const leftHasValidDate = leftStart > 0;
    const rightHasValidDate = rightStart > 0;

    if (leftHasValidDate !== rightHasValidDate) {
      return leftHasValidDate ? -1 : 1;
    }

    if (!leftHasValidDate && !rightHasValidDate) {
      return left.id.localeCompare(right.id);
    }

    const leftIsPast = leftEnd < nowMs;
    const rightIsPast = rightEnd < nowMs;

    if (leftIsPast !== rightIsPast) {
      return leftIsPast ? 1 : -1;
    }

    if (leftIsPast && rightIsPast) {
      return rightEnd - leftEnd || left.id.localeCompare(right.id);
    }

    return leftStart - rightStart || left.id.localeCompare(right.id);
  });
}

export function buildMyAdventureBuckets<T extends AdventureForGrouping>(
  adventures: T[],
  userId: string,
  membershipByAdventureId: Record<string, string | null | undefined>,
  now = new Date(),
): MyAdventureBuckets<T> {
  const nowMs = now.getTime();

  const organising: Array<T & { my_adventure_status: MyAdventureStatus; is_past: boolean }> = [];
  const upcoming: Array<T & { my_adventure_status: MyAdventureStatus; is_past: boolean }> = [];
  const past: Array<T & { my_adventure_status: MyAdventureStatus; is_past: boolean }> = [];

  for (const adventure of adventures) {
    const isOrganising = adventure.user_id === userId;
    const membershipStatus = membershipByAdventureId[adventure.id];

    if (!isOrganising && membershipStatus !== 'joined' && membershipStatus !== 'interested' && membershipStatus !== 'waitlist') {
      continue;
    }

    const isPast = getAdventureEndTime(adventure) < nowMs;
    const myAdventureStatus: MyAdventureStatus = isOrganising
      ? 'organising'
      : membershipStatus === 'joined'
      ? 'going'
      : membershipStatus === 'waitlist'
        ? 'waitlist'
        : 'interested';

    const nextAdventure = {
      ...adventure,
      my_adventure_status: myAdventureStatus,
      is_past: isPast,
    };

    if (isPast) {
      past.push(nextAdventure);
      continue;
    }

    if (isOrganising) {
      organising.push(nextAdventure);
      continue;
    }

    upcoming.push(nextAdventure);
  }

  organising.sort((left, right) => getAdventureStartTime(left) - getAdventureStartTime(right));
  upcoming.sort((left, right) => getAdventureStartTime(left) - getAdventureStartTime(right));
  past.sort((left, right) => getAdventureEndTime(right) - getAdventureEndTime(left));

  return {
    organising,
    upcoming,
    past,
  };
}
